<template>
    <div class="ls-edition-usage relative hidden lg:block">
        <!-- opened=false: 仅显示 Rocket Icon -->
        <template v-if="!opened">
            <div ref="triggerRef" class="collapsed-trigger" @click="togglePanel">
                <PtIcon :icon="RocketIcon" class="rocket-icon" />
            </div>
        </template>

        <!--
            ls-edition-usage-container 显示逻辑：
            - opened=true: 一直显示，无动画
            - opened=false: 由 isPanelVisible 控制，有动画
        -->
        <transition :name="!opened ? 'panel-expand' : ''">
            <div ref="panelRef" v-show="opened || isPanelVisible" class="ls-edition-usage-container" :class="{ compact: !opened }">
                <!-- Header: Plan usage + Badge + Collapse Icon -->
                <div class="usage-header" @click="toggleCollapse">
                    <div class="header-left">
                        <span class="usage-title">{{ t('planUsage') }}</span>
                        <span class="edition-badge">
                            {{ expireDateText || editionBadgeText }}
                        </span>
                    </div>
                    <PtIcon
                        :icon="ArrowDown"
                        class="collapse-icon"
                        :class="{ 'rotate-180': isCollapsed }"
                    />
                </div>

                <!-- Content (可折叠) -->
                <transition name="collapse">
                    <div v-show="!isCollapsed" class="usage-content">
                        <!-- Skeleton Loading -->
                        <template v-if="isLoading">
                            <div class="usage-skeleton">
                                <div v-for="i in 3" :key="i" class="skeleton-item">
                                    <div class="skeleton-row">
                                        <div class="skeleton-label" />
                                        <div class="skeleton-value" />
                                    </div>
                                    <div class="skeleton-progress" />
                                </div>
                            </div>
                        </template>

                        <!-- Error State -->
                        <template v-else-if="error">
                            <div class="error-state">
                                <span class="error-text">{{ t('loadError') }}</span>
                                <button class="retry-button" @click="fetchLsPlgUsage">
                                    {{ t('retry') }}
                                </button>
                            </div>
                        </template>

                        <!-- Usage List -->
                        <template v-else>
                            <div class="usage-list">
                                <UserUsage />
                            </div>
                        </template>
                    </div>
                </transition>

                <!-- Upgrade Button (条件展示: 仅免费和试用用户) -->
                <div class="px-2 pb-3">
                    <button
                        v-if="showUpgradeButton"
                        class="upgrade-button"
                        @click.stop="handleUpgradeWithTracking"
                    >
                        <PtIcon :icon="RocketIcon" class="button-icon" />
                        {{ t('upgrade') }}
                    </button>
                </div>

            </div>
        </transition>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onBeforeMount, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalStorage, onClickOutside } from '@vueuse/core';
import { PtIcon } from 'patsnap-biz';
import { useLsEditionUsage } from '../../composables/use-ls-edition-usage';
import RocketIcon from '@patsnap-ui/icon/assets/solid/rocket.svg';
import ArrowDown from '@patsnap-ui/icon/assets/solid/ArrowDown.svg';
import { useTrack } from '../../../../composables/use-track';
import UserUsage from './components/UserUsage.vue';

// 组件独立的国际化配置
const messages = {
    cn: {
        planUsage: '方案使用情况',
        earnCredits: '赚取积分',
        creditsPerMonth: '积分/月',
        projects: '项目',
        upgrade: '升级',
        loading: '加载中...',
        loadError: '加载失败',
        retry: '重试',
        noUsageData: '暂无用量数据',
        daysLeft: '{n} 天剩余',
        dayLeft: '{n} 天剩余',
    },
    en: {
        planUsage: 'Plan usage',
        earnCredits: 'Earn credits',
        creditsPerMonth: 'Credits/month',
        projects: 'Projects',
        upgrade: 'Upgrade',
        loading: 'Loading...',
        loadError: 'Failed to load',
        retry: 'Retry',
        noUsageData: 'No usage data',
        daysLeft: '{n} days left',
        dayLeft: '{n} day left',
    },
    jp: {
        planUsage: 'プラン使用状況',
        earnCredits: 'ポイントを獲得する',
        creditsPerMonth: 'ポイント/月',
        projects: 'プロジェクト',
        upgrade: 'アップグレード',
        loading: '読み込み中...',
        loadError: '読み込み失敗',
        retry: '再試行',
        noUsageData: '使用データがありません',
        daysLeft: '残り {n} 日',
        dayLeft: '残り {n} 日',
    },
    tw: {
        planUsage: '方案使用情況',
        earnCredits: '額外積分',
        creditsPerMonth: '積分/月',
        projects: '專案',
        upgrade: '升級',
        loading: '載入中...',
        loadError: '載入失敗',
        retry: '重試',
        noUsageData: '暫無用量資料',
        daysLeft: '{n} 天剩餘',
        dayLeft: '{n} 天剩餘',
    },
};

const { t } = useI18n({
    messages,
});

// 埋点 API
const track = useTrack();

// Props
const props = withDefaults(
    defineProps<{
        opened?: boolean;
    }>(),
    {
        opened: true,
    }
);

const {
    // 数据状态
    isLoading,
    error,
    userEdition,
    editionBadgeText,
    expireDaysLeft,

    showUpgradeButton,

    // 工具函数
    handleUpgrade,
    fetchLsPlgUsage,
} = useLsEditionUsage();

// 带埋点的升级函数
const handleUpgradeWithTracking = () => {
    // 埋点
    track({
        eventType: 'FF LS PLG',
        moduleType: 'LS',
        sourceType: 'ls-plan-usage-upgrade',
        otherParam: userEdition.value,
    });
    // 调用原始升级函数
    handleUpgrade();
};

// 格式化过期倒计时文本（国际化）
const expireDateText = computed(() => {
    if (userEdition.value === 'free_trial') {
        const days = expireDaysLeft.value ?? 0;
        // 0 或 1 天使用单数形式，其他用复数形式
        const key = days === 0 || days === 1 ? 'dayLeft' : 'daysLeft';
        return t(key, { n: days });
    }
    return ''
});

// opened=false 时，临时展开面板的状态
const isPanelVisible = ref(false);

// opened=true 时，内容折叠状态 - 使用 localStorage 持久化，默认展开（false）
const isCollapsed = useLocalStorage('ls-edition-usage-collapsed', false);

// 面板和触发器的 ref（用于点击外部关闭）
const panelRef = ref() as Ref<HTMLElement>;
const triggerRef = ref<HTMLElement>();

// 切换临时面板显示（opened=false 时使用）
const togglePanel = () => {
    isPanelVisible.value = !isPanelVisible.value;
};

// 切换内容折叠状态（opened=true 时使用）
const toggleCollapse = () => {
    isCollapsed.value = !isCollapsed.value;
};

// 点击外部关闭浮动面板（仅在 opened=false 且面板可见时生效）
onClickOutside(
    panelRef as any,
    () => {
        if (!props.opened && isPanelVisible.value) {
            isPanelVisible.value = false;
        }
    },
    { ignore: [triggerRef as any] }
);

onBeforeMount(() => {
    fetchLsPlgUsage();
});
</script>

<style lang="scss" scoped>
.ls-edition-usage {
  z-index: 100;

  &-container {
    @apply bg-white rounded-lg;
    width: 196px;
    z-index: 100;

    // opened=true 时的样式（正常模式）
    &:not(.compact) {
      @apply shadow-md;
    }

    // opened=false 时的样式（紧凑浮动模式）
    &.compact {
      @apply absolute bottom-0 shadow-xl;
      left: 64px; // 在 rocket icon 右侧
    }
  }
}

// opened=false 时的触发器 (只显示 Rocket Icon)
.collapsed-trigger {
  @apply w-8 h-8 rounded-full flex items-center justify-center cursor-pointer;
  @apply shadow-lg hover:shadow-xl transition-all duration-300;
  background: linear-gradient(134deg, rgba(230, 248, 221, 1) 0%, rgba(237, 252, 255, 1) 28%, rgba(221, 240, 248, 1) 63%, rgba(185, 217, 255, 1) 99.92%);
  z-index: 99999;

  .rocket-icon {
    @apply w-6 h-6 fill-eureka-500 text-eureka-500;
  }

  &:hover {
    transform: scale(1.05);
  }
}

.usage-header {
  @apply flex items-center justify-between p-3 cursor-pointer rounded-lg;

  &:hover {
    @apply bg-grey-20;
  }

  .header-left {
    @apply flex items-center gap-2;
  }

  .usage-title {
    @apply text-xs text-[#020A1A] font-medium;
  }

  .edition-badge {
    @apply px-1 py-0.5 text-xs rounded;
    @apply bg-eureka-40 text-eureka-500;
  }

  .collapse-icon {
    @apply w-6 h-6 text-grey-450 transition-transform duration-300;
  }
}

.usage-content {
  @apply p-3 pt-0;
}

.usage-list {
  @apply flex flex-col gap-4 mt-3;
}

.empty-usage {
  // @apply py-4 text-center;

  // .empty-text {
  //   @apply text-xs text-grey-200;
  // }
}

.error-state {
  @apply py-4 flex flex-col items-center gap-2;

  .error-text {
    @apply text-xs text-red-600;
  }

  .retry-button {
    @apply px-3 py-1 text-xs text-eureka-500 border border-eureka-500 rounded;
    @apply hover:bg-eureka-40 transition-colors duration-200 cursor-pointer;
  }
}

.upgrade-button {
  @apply w-full flex items-center justify-center gap-1.5 px-3 py-2;
  @apply text-xs font-medium text-eureka-500;
  @apply rounded border-none cursor-pointer;
  background: linear-gradient(134deg, rgba(230, 248, 221, 1) 0%, rgba(237, 252, 255, 1) 28%, rgba(221, 240, 248, 1) 63%, rgba(185, 217, 255, 1) 99.92%);

  .button-icon {
    @apply w-5 h-5 fill-eureka-500;
  }
}

// 骨架屏样式
.usage-skeleton {
  @apply flex flex-col gap-4 mt-3;
}

.skeleton-item {
  @apply flex flex-col gap-1.5;
}

.skeleton-row {
  @apply flex items-center justify-between;
}

.skeleton-label {
  @apply h-3 w-20 bg-grey-60 rounded animate-pulse;
}

.skeleton-value {
  @apply h-3 w-16 bg-grey-60 rounded animate-pulse;
}

.skeleton-progress {
  @apply h-1.5 w-full bg-grey-40 rounded-full animate-pulse;
}

// 面板展开/收起动画（opened=false 时）
.panel-expand-enter-active,
.panel-expand-leave-active {
  transition: all 0.3s ease;
}

.panel-expand-enter-from,
.panel-expand-leave-to {
  opacity: 0;
  transform: translateX(-10px) scale(0.95);
}

.panel-expand-enter-to,
.panel-expand-leave-from {
  opacity: 1;
  transform: translateX(0) scale(1);
}

// 内容折叠动画（opened=true 时）
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  max-height: 500px;
  opacity: 1;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
  overflow: hidden;
}
</style>
